import * as React from 'react';
import styled, { css } from 'styled-components';
import {COLORS, ComponentTextStyle, focusStyles} from '../styles';
import {SystemIcons} from '../icons';
import {Size} from '../types';
import {ComponentLStyling, ComponentMStyling, ComponentSStyling} from '../styles';
import { DropdownItem } from '../Dropdown';
import {defaultOnMouseDownHandler} from '../common';

export interface MenuItemProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'id' | 'disabled' | 'onMouseDown' | 'onClick'> {
   item: DropdownItem;
   onClickHandler: (e: React.MouseEvent) => void;
   active: boolean;
   id: string;
   size?: Size;
   iconRight?: React.ReactElement;
}

export const DropdownButton = styled.button<{ size?: Size }>`
  ${(props) => DropdownButtonCSS(props.size)}
`;

export const ItemLabel = styled.div``;

export const ItemIcon = styled.div``;

export const ItemIconRight = styled.div`
  align-items: center;
  display: flex;
`;

export const ItemContent = styled.div<{size:Size}>`
    margin: ${props=>props.size===Size.Small?'auto 0 auto 10px':(props.size===Size.Large?'auto 0 auto 26px':'auto 0 auto 18px')};
    display: flex;
    align-items: center;
    flex-grow: 2;

    ${ItemLabel} {
      margin: ${props => props.size === Size.Small || props.size === Size.Medium ? '14px 0' : '16px 0'};
      flex: 1 0 calc(100% - 40px);
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    ${ItemIcon} {
      margin-right: 8px;
      svg {
        display: block;
        margin: auto;
        height:${props=>props.size===Size.Small?'20px':''};
        width:${props=>props.size===Size.Small?'20px':''};
      }
    }

    ${ItemIconRight} {
      flex-grow: 2;

      .extraMargin {
        position: absolute;
        right: ${props=>props.size===Size.Small?'16px':'24px'};
      }
    }
`;


export const DropdownButtonCSS = (size?: Size) => {
  return css`
    ${size == Size.Small && ComponentSStyling(ComponentTextStyle.Regular, COLORS.neutral_600)}
    ${size == Size.Medium && ComponentMStyling(ComponentTextStyle.Regular, COLORS.neutral_600)}
    ${size == Size.Large && ComponentLStyling(ComponentTextStyle.Regular, COLORS.neutral_600)}

    position: relative;
    text-decoration: none;
    display: flex;
    flex-direction: row;
    align-items: center;
    min-height: ${size == Size.Large ? '64px' : size == Size.Medium ? '56px' : '48px'};
    background-color: ${COLORS.white};
    border: none;
    cursor: pointer;
    background: none;
    width: 100%;
    text-align: left;

    span {
      margin: auto 0 auto 16px;
      line-height: 48px;
    }

    ${ItemLabel}, ${ItemIcon} {
      color: ${COLORS.neutral_600};
    }

    &:hover:not(.active):not(:disabled):not(.disabled-state),
    &.hover-state:not(.active):not(:disabled):not(.disabled-state) {
      background: ${COLORS.primary_20};
      ${ItemLabel}, ${ItemIcon} {
        color: ${COLORS.primary_700};
      }
    }

    &:focus, &.focus-state, &.focus-visible {
      ${focusStyles}
    }

    &:active:not(.active):not(:disabled):not(.disabled-state), &.active-state {
      background: ${COLORS.primary_100};
      ${ItemLabel}, ${ItemIcon} {
        color: ${COLORS.primary_800};
      }
    }

    &.active {
      background: ${COLORS.neutral_20};

      ${ItemLabel}, ${ItemIcon} {
        color: ${COLORS.neutral_800};
      }

      &::after {
        position: absolute;
        content: ' ';
        width: 4px;
        top: ${size == Size.Large ? '8px' : size == Size.Medium ? '6px' : '4px'};
        bottom: ${size == Size.Large ? '8px' : size == Size.Medium ? '6px' : '4px'};
        left: 4px;
        background-color: ${COLORS.primary_500};

        border-radius: 4px;
      }
    }


    &.locked {
      background: none;
      cursor: not-allowed;
      
      ${ItemIcon}, ${ItemLabel} {
        color: ${COLORS.neutral_500};
      }
    }

    &.active:hover:not(:disabled):not(.locked), &.active-hover-state {
      background-color: ${COLORS.primary_20};

      ${ItemIcon}, ${ItemLabel} {
        color: ${COLORS.primary_700};
      }      

      &::after {
        background-color: ${COLORS.primary_700};
      }
    }

    &.active:active:not(:disabled):not(.locked), &.active-active-state {
      background-color: ${COLORS.primary_100};

      ${ItemIcon}, ${ItemLabel} {
        color: ${COLORS.primary_800};
      }

      &::after {
        background-color: ${COLORS.primary_800};
      }
    }

    &.active.locked {
      &::after {
        background-color: ${COLORS.neutral_500};
      } 
    }

    &.active:disabled:not(.locked) {
      &::after {
        background-color: ${COLORS.neutral_300};
      }     
    }

    &:disabled:not(.locked), &.disabled-state {
      background: none;
      cursor: not-allowed;
      
      ${ItemIcon}, ${ItemLabel} {
        color: ${COLORS.neutral_300} !important;
      }
    }

    &.dropdown-hover:not(:disabled) {
      background-color: ${COLORS.primary_20};
      color: ${COLORS.primary_600};
    }
  `;
};

const MenuItem = React.forwardRef<HTMLButtonElement, MenuItemProps>(({
                                                                       active,
                                                                       item,
                                                                       onClickHandler,
                                                                       size = Size.Medium,
                                                                       className,
                                                                       tabIndex,
                                                                       iconRight,
                                                                       id,
                                                                       ...rest
                                                                     }: MenuItemProps, ref) => {
  return (
    <DropdownButton
        type="button"
        size={size}
        disabled={item.disabled || item.locked}
        tabIndex={tabIndex}
        onMouseDown={defaultOnMouseDownHandler}
        ref={ref}
        onClick={onClickHandler}
        className={ className + ' ' + (item.locked ? 'locked' : '') + ' ' + (item.showDividerAbove ? 'show-divider-above' : '') + ' ' + (active ? 'active' : '')}
        id={id}
        {...rest}>
        {item.showDividerAbove && <div className="divider" />}
        <ItemContent size={size}>
          {item.icon && <ItemIcon>{item.icon}</ItemIcon>}
          <ItemLabel>
              <div>
                <div>{item.displayLabel ?? item.value}</div>
                {item.noteLabel && <div style={{ fontSize: '12px', lineHeight: '16px' }}>{item.noteLabel}</div>}
              </div>
            {
              item.locked &&
              <ItemIconRight>
                <SystemIcons.LockedOn color={COLORS.neutral_400} className="extraMargin" size="24px"/>
              </ItemIconRight>
            }
            {
              !item.locked && !!iconRight &&
              <ItemIconRight>
                {React.cloneElement(iconRight, {className: 'extraMargin', size: '24px',})}
              </ItemIconRight>
            }

          </ItemLabel>
        </ItemContent>
  </DropdownButton>
  );
});

export default MenuItem;
